import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Truck, 
  Shield, 
  Clock, 
  CheckCircle, 
  Users, 
  FileText, 
  TrendingUp, 
  Star,
  ChevronRight,
  Play,
  ArrowRight,
  Zap,
  Target,
  Award,
  BarChart3,
  Calendar,
  AlertTriangle,
  PhoneCall,
  Mail,
  Menu,
  X
} from 'lucide-react';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/onboarding');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center min-w-0">
              <img 
                src="/Himil-logo-header.png" 
                alt="Himil Fleet Logo" 
                className="h-6 sm:h-8 w-auto flex-shrink-0"
              />
              <span className="ml-2 text-lg sm:text-xl font-bold text-gray-900 truncate">Himil Fleet</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">Features</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">Pricing</a>
              <button 
                onClick={() => navigate('/login')}
                className="text-gray-600 hover:text-gray-900 transition-colors font-medium"
              >
                Sign In
              </button>
              <button 
                onClick={handleGetStarted}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors font-medium touch-manipulation"
              >
                Get Started Free
              </button>
            </div>
            
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-600 hover:text-gray-900 active:text-gray-700 transition-colors p-2 -mr-2 touch-manipulation"
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMenuOpen ? <X className="h-5 w-5 sm:h-6 sm:w-6" /> : <Menu className="h-5 w-5 sm:h-6 sm:w-6" />}
              </button>
            </div>
          </div>
          
          {/* Mobile menu */}
          {isMenuOpen && (
            <div className="md:hidden border-t border-gray-200 bg-white shadow-lg">
              <div className="px-4 py-4 space-y-1">
                <a 
                  href="#features" 
                  onClick={() => setIsMenuOpen(false)}
                  className="block py-3 px-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors rounded-lg font-medium touch-manipulation"
                >
                  Features
                </a>
                <a 
                  href="#pricing" 
                  onClick={() => setIsMenuOpen(false)}
                  className="block py-3 px-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors rounded-lg font-medium touch-manipulation"
                >
                  Pricing
                </a>
                <button 
                  onClick={() => {
                    setIsMenuOpen(false);
                    navigate('/login');
                  }}
                  className="block w-full text-left py-3 px-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors rounded-lg font-medium touch-manipulation"
                >
                  Sign In
                </button>
                <div className="pt-2">
                  <button 
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleGetStarted();
                    }}
                    className="block w-full bg-blue-600 text-white px-4 py-4 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors font-medium text-center shadow-sm touch-manipulation"
                  >
                    Get Started Free
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 sm:py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800 mb-4 sm:mb-6">
                <Zap className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                <span className="whitespace-nowrap">The Modern Fleet Management Solution</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4 sm:mb-6">
                Stop the 
                <span className="text-blue-600"> business chaos.</span>
                <br />Start profiting.
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed px-2 sm:px-0">
                Himil Fleet is your complete trucking business management solution. Documents, finances, compliance, and operations - 
                <span className="font-semibold text-gray-800"> everything you need to run a profitable fleet from one dashboard.</span>
              </p>
              
              <div className="mb-4 sm:mb-6 px-2 sm:px-0">
                <button
                  onClick={handleGetStarted}
                  className="w-full sm:w-auto bg-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-all duration-200 font-semibold text-base sm:text-lg flex items-center justify-center group shadow-lg hover:shadow-xl touch-manipulation"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
              
              <div className="flex items-center justify-center lg:justify-start text-xs sm:text-sm text-gray-500 px-2 sm:px-0">
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 mr-2 flex-shrink-0" />
                <span className="text-center lg:text-left">Free 30-day trial • No credit card required • Setup in 5 minutes</span>
              </div>
            </div>
            
            <div className="relative mt-8 lg:mt-0 order-first lg:order-last">
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden transform lg:rotate-3 lg:hover:rotate-0 transition-transform duration-500 mx-4 sm:mx-0">
                <div className="bg-blue-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center">
                  <div className="flex space-x-1 sm:space-x-2">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-400 rounded-full"></div>
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full"></div>
                  </div>
                  <span className="ml-3 sm:ml-4 text-white font-medium text-sm sm:text-base">Himil Fleet Dashboard</span>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center justify-between p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center min-w-0 flex-1">
                        <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mr-2 sm:mr-3 flex-shrink-0" />
                        <span className="font-medium text-gray-900 text-sm sm:text-base truncate">All drivers compliant</span>
                      </div>
                      <span className="text-green-600 font-semibold text-sm sm:text-base ml-2">100%</span>
                    </div>
                    <div className="flex items-center justify-between p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center min-w-0 flex-1">
                        <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mr-2 sm:mr-3 flex-shrink-0" />
                        <span className="font-medium text-gray-900 text-sm sm:text-base truncate">Documents up to date</span>
                      </div>
                      <span className="text-blue-600 font-semibold text-sm sm:text-base ml-2">45/45</span>
                    </div>
                    <div className="flex items-center justify-between p-3 sm:p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center min-w-0 flex-1">
                        <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 mr-2 sm:mr-3 flex-shrink-0" />
                        <span className="font-medium text-gray-900 text-sm sm:text-base truncate">Expiring soon</span>
                      </div>
                      <span className="text-amber-600 font-semibold text-sm sm:text-base ml-2">3 items</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              Every trucking company faces the same business challenges
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Drowning in paperwork, losing money to inefficient operations, and struggling to track true profitability.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center p-6 sm:p-8 bg-white rounded-xl shadow-sm border border-gray-200 mx-4 sm:mx-0">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <AlertTriangle className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">DOT Violations Cost $$$</h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                Average fine of $3,200 per violation. Plus the downtime, legal fees, and reputation damage.
              </p>
            </div>
            
            <div className="text-center p-6 sm:p-8 bg-white rounded-xl shadow-sm border border-gray-200 mx-4 sm:mx-0">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-amber-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">No Financial Visibility</h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                Can't track true profitability per load or vehicle. Hidden costs eat away profits without notice.
              </p>
            </div>
            
            <div className="text-center p-6 sm:p-8 bg-white rounded-xl shadow-sm border border-gray-200 mx-4 sm:mx-0">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Operational Inefficiencies</h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                Lost documents, manual processes, and disconnected systems waste time and money every day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="features" className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              Complete Business Management for Your Fleet
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Documents, finances, compliance, and operations - everything you need to run a profitable trucking business in one platform.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center mb-12 sm:mb-20">
            <div className="px-4 sm:px-0">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800 mb-4 sm:mb-6">
                <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                Driver Document Management
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                Complete Driver Qualification Files
              </h3>
              <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                Track all driver documents including CDL licenses, medical certificates, drug test results, 
                background checks, and training certificates. Get automatic expiration alerts.
              </p>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm sm:text-base">CDL licenses & endorsements (Hazmat, etc.)</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm sm:text-base">Medical certificates & drug test results</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm sm:text-base">Employment verification & training records</span>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-blue-50 p-8 rounded-2xl">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">Driver Documents</h4>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">3 drivers</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <Users className="h-4 w-4 text-green-600 mr-3" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">John Smith</p>
                      <p className="text-sm text-green-600">CDL, Medical Cert, Drug Test ✓</p>
                    </div>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="flex items-center p-3 bg-red-50 rounded-lg border border-red-200">
                    <Users className="h-4 w-4 text-red-600 mr-3" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Mike Davis</p>
                      <p className="text-sm text-red-600">CDL expires in 5 days!</p>
                    </div>
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                  </div>
                  <div className="flex items-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <Users className="h-4 w-4 text-amber-600 mr-3" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Sarah Johnson</p>
                      <p className="text-sm text-amber-600">Medical cert due in 30 days</p>
                    </div>
                    <Clock className="h-5 w-5 text-amber-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Vehicle Documents Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div className="order-2 lg:order-1">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl">
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900">Vehicle Fleet Status</h4>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">12 vehicles</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                      <Truck className="h-4 w-4 text-green-600 mr-3" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Vehicle #124</p>
                        <p className="text-sm text-green-600">Registration, IFTA permit ✓</p>
                      </div>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                    <div className="flex items-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <Truck className="h-4 w-4 text-amber-600 mr-3" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Vehicle #89</p>
                        <p className="text-sm text-amber-600">Annual inspection due</p>
                      </div>
                      <Clock className="h-5 w-5 text-amber-500" />
                    </div>
                    <div className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <Truck className="h-4 w-4 text-blue-600 mr-3" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Vehicle #156</p>
                        <p className="text-sm text-blue-600">Recent maintenance logged</p>
                      </div>
                      <FileText className="h-5 w-5 text-blue-500" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 mb-6">
                <Truck className="h-4 w-4 mr-2" />
                Vehicle Management
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Complete Vehicle Records & Inspections
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                Track vehicle registrations, annual inspections, IFTA permits, maintenance records, and more. 
                Conduct digital vehicle inspections with photo uploads directly from mobile devices.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700">Vehicle registration & title documents</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700">Annual inspections & emissions certificates</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700">IFTA permits & operating authority</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700">Digital inspections with photo uploads</span>
                </div>
              </div>
            </div>
          </div>

          {/* Finance Management Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800 mb-6">
                <TrendingUp className="h-4 w-4 mr-2" />
                Financial Management
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Complete Financial Control & Profitability
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                Track all income, expenses, and costs in real-time. Generate professional invoices for drivers, 
                monitor fuel costs, maintenance expenses, and calculate true profitability per load and vehicle.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700">Income tracking & load revenue management</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700">Driver invoicing & settlement statements</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700">Fuel, maintenance & operational cost tracking</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700">Profit & loss reports by vehicle/driver</span>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-8 rounded-2xl">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">Financial Dashboard</h4>
                  <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">This Month</span>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center">
                      <TrendingUp className="h-4 w-4 text-green-600 mr-3" />
                      <span className="font-medium text-gray-900">Total Revenue</span>
                    </div>
                    <span className="text-green-600 font-bold">$47,850</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center">
                      <TrendingUp className="h-4 w-4 text-red-600 mr-3 transform rotate-180" />
                      <span className="font-medium text-gray-900">Total Expenses</span>
                    </div>
                    <span className="text-red-600 font-bold">$31,240</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center">
                      <BarChart3 className="h-4 w-4 text-blue-600 mr-3" />
                      <span className="font-medium text-gray-900">Net Profit</span>
                    </div>
                    <span className="text-blue-600 font-bold">$16,610</span>
                  </div>
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">Outstanding Invoices</span>
                      <span className="text-amber-600 font-bold">$8,920</span>
                    </div>
                    <div className="text-xs text-amber-600">3 drivers pending payment</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-20">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Driver Documents</h3>
              <p className="text-gray-600 mb-4">
                Complete driver qualification files with CDL licenses, medical certificates, drug tests, background checks, and training records.
              </p>
              <div className="text-sm text-gray-500 space-y-1">
                <div>• CDL Licenses & Endorsements</div>
                <div>• Medical Certificates</div>
                <div>• Drug & Alcohol Tests</div>
                <div>• MVR License Checks</div>
              </div>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Truck className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Vehicle Records</h3>
              <p className="text-gray-600 mb-4">
                Track vehicle registrations, inspections, permits, and maintenance records. Digital vehicle inspections with photo uploads.
              </p>
              <div className="text-sm text-gray-500 space-y-1">
                <div>• Vehicle Registration & Title</div>
                <div>• Annual Inspections</div>
                <div>• IFTA & Operating Permits</div>
                <div>• Maintenance Records</div>
              </div>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Load & Company Docs</h3>
              <p className="text-gray-600 mb-4">
                Manage Bills of Lading, delivery receipts, insurance certificates, and company permits all in one place.
              </p>
              <div className="text-sm text-gray-500 space-y-1">
                <div>• Bills of Lading (BOL)</div>
                <div>• Insurance Certificates</div>
                <div>• DOT Authority & MC Numbers</div>
                <div>• Delivery Receipts</div>
              </div>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Financial Management</h3>
              <p className="text-gray-600 mb-4">
                Complete financial control with income tracking, expense management, driver invoicing, and profitability analysis.
              </p>
              <div className="text-sm text-gray-500 space-y-1">
                <div>• Income & Revenue Tracking</div>
                <div>• Driver Invoice Generation</div>
                <div>• Expense & Cost Management</div>
                <div>• Profit & Loss Reports</div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Pricing Teaser */}
      <section id="pricing" className="py-12 sm:py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
            ROI in your first month. Guaranteed.
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 px-4">
            Most customers save more in avoided fines than they pay for the entire year.
          </p>
          
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-6 sm:p-8 mb-6 sm:mb-8 mx-4 sm:mx-0">
            <div className="flex flex-col sm:flex-row items-center justify-center mb-6">
              <div className="text-3xl sm:text-4xl font-bold text-gray-900">$10</div>
              <div className="text-gray-600 sm:ml-2 text-base sm:text-lg mt-1 sm:mt-0">per truck per month</div>
            </div>
            <div className="space-y-3 mb-6 sm:mb-8">
              <div className="flex items-center justify-center text-left">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-3 flex-shrink-0" />
                <span className="text-gray-700 text-sm sm:text-base">Unlimited document storage</span>
              </div>
              <div className="flex items-center justify-center text-left">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-3 flex-shrink-0" />
                <span className="text-gray-700 text-sm sm:text-base">Smart expiration alerts</span>
              </div>
              <div className="flex items-center justify-center text-left">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-3 flex-shrink-0" />
                <span className="text-gray-700 text-sm sm:text-base">DOT audit reports</span>
              </div>
              <div className="flex items-center justify-center text-left">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-3 flex-shrink-0" />
                <span className="text-gray-700 text-sm sm:text-base">24/7 support</span>
              </div>
            </div>
            <button 
              onClick={handleGetStarted}
              className="bg-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors font-semibold text-base sm:text-lg w-full mb-3 sm:mb-4 touch-manipulation"
            >
              Start Free 30-Day Trial
            </button>
            <p className="text-xs sm:text-sm text-gray-500">No credit card required • Cancel anytime</p>
          </div>
          
          <p className="text-gray-600 text-sm sm:text-base px-4">
            <strong>Money-back guarantee:</strong> If we don't save you at least $100 in your first month, we'll refund your entire payment.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 sm:py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 px-4">
            Ready to run a profitable trucking business?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-6 sm:mb-8 px-4">
            Join trucking companies who have complete control over their finances and operations.
          </p>
          
          <div className="flex flex-col gap-4 justify-center items-center mb-6 sm:mb-8">
            <button 
              onClick={handleGetStarted}
              className="bg-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors font-semibold text-base sm:text-lg flex items-center group w-full sm:w-auto touch-manipulation"
            >
              Start Free Trial Now
              <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <div className="flex items-center text-gray-300 text-sm sm:text-base">
              <PhoneCall className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0" />
              <span>Or call us: (555) 123-4567</span>
            </div>
          </div>
          
          <div className="flex items-center justify-center text-xs sm:text-sm text-gray-400 px-4">
            <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 mr-2 flex-shrink-0" />
            <span className="text-center">Setup takes 5 minutes • Support team available 24/7</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="/Himil-logo-header.png" 
                  alt="Himil Fleet Logo" 
                  className="h-8 w-auto"
                />
                <span className="ml-2 text-xl font-bold">Himil Fleet</span>
              </div>
              <p className="text-gray-400">
                The complete business management platform for trucking companies.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Integrations</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Training</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Status</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Himil Fleet. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;